import { DealerProfileSettingsActionTypes as AT } from './constants';

/**
 * @typedef {import('./ServicesPanel/services/index.name').DealerProfileServiceCategoryValue} DealerProfileServiceCategoryValue
 */

/**
 * @typedef {<T extends string>({dealerId}: {dealerId: T}) => {type: 'TOGGLE_ALL_DEALER_PROFILE_SETTINGS_OPTIONS', payload: {dealerId: string}} ToggleAllDealerProfileSettingsOptions
 * @typedef {() => {type: 'TOGGLE_APPLY_CHANGES_TO_ALL'} ToggleApplyChangesToAll
 * @typedef {<T extends string, U extends string, V extends string>({dealerId, serviceName, debugLabel}: {dealerId: T, serviceName: U, debugLabel: V}) => {type: 'UPDATE_PROFILE_SETTING_FOR_DEALER', payload: { dealerId: T, serviceName: U, debugLabel: V }} UpdateProfileSettingForDealer
 **/

/**
 * @description
 * used to set branch normalized services dictionary
 * from branches information in redux
 *
 * @param {object} branchesDictionary - dealer branches dictionary indexed by id
 * @returns {{type: 'SET_INITIAL_DEALER_PROFILE_SETTINGS_OPTIONS', payload: {branchesDictionary: Record<string, object>}}}
 */
export const setInitialDealerProfileSettingsOptions = ({
  branchesDictionary,
}) => ({
  type: AT.SET_INITIAL_DEALER_PROFILE_SETTINGS_OPTIONS,
  payload: { branchesDictionary },
});

/**
 *
 * @type {UpdateProfileSettingForDealer}
 * @param {Object} param0
 * @param {string} param0.dealerId          - dealer id
 * @param {string} param0.serviceName       - service name identifier used by backend
 * @param {string} param0.debugLabel        - used to see i18n label visible in UI
 */
export const updateProfileSettingForDealer = ({
  dealerId,
  serviceName,
  debugLabel,
}) => ({
  type: AT.UPDATE_PROFILE_SETTING_FOR_DEALER,
  payload: { dealerId, serviceName, debugLabel },
});

/**
 * @description
 * toggles all checkboxes for a given category
 * @const toggleAllDealerProfileSettingsOptions
 * @type {ToggleAllDealerProfileSettingsOptions}
 */
export const toggleAllDealerProfileSettingsOptions = ({ dealerId }) => ({
  type: AT.TOGGLE_ALL_DEALER_PROFILE_SETTINGS_OPTIONS,
  payload: { dealerId },
});

export const setDealerIdBeingEdited = ({ dealerId }) => ({
  type: AT.SET_DEALER_ID_BEING_EDITED,
  payload: { dealerId },
});

export const toggleApplyChangesToAll = () => ({
  type: AT.TOGGLE_APPLY_CHANGES_TO_ALL,
});

/**
 * @description
 * form submit actions
 */

/**
 * @param {object} param0
 * @param {boolean} [param0.isAConfirmSaveChangesEvent] - used to let us know user has saved changes from a modal
 * @returns {{type: 'SUBMIT_DEALER_PROFILE_SETTINGS_BEGIN', payload: { isAConfirmSaveChangesEvent: boolean }}}
 */
export const submitDealerProfileSettingsBegin = ({
  isAConfirmSaveChangesEvent,
} = {}) => ({
  type: AT.SUBMIT_DEALER_PROFILE_SETTINGS_BEGIN,
  payload: { isAConfirmSaveChangesEvent },
});

export const submitDealerProfileSettingsSuccess = () => ({
  type: AT.SUBMIT_DEALER_PROFILE_SETTINGS_SUCCESS,
});

export const submitDealerProfileSettingsFail = ({ error }) => ({
  type: AT.SUBMIT_DEALER_PROFILE_SETTINGS_FAIL,
  payload: { error },
});

export const resetAllSubmissionStates = () => ({
  type: AT.RESET_ALL_SUBMISSION_STATES,
});

/**
 * @description
 *
 * sets category tabs in edit panel
 * i.e. General Services, Vehicle Purchase Service, and Workshop Services
 * @param {DealerProfileServiceCategoryValue} category
 * @returns {{type: 'SET_SELECTED_DEALER_PROFILE_SETTINGS_CATEGORY_TAB', payload: {category: DealerProfileServiceCategoryValue} }}
 */
export const setSelectedDealerProfileSettingsCategoryTab = ({
  selectedDealerProfileSettingsCategory,
}) => ({
  type: AT.SET_SELECTED_DEALER_PROFILE_SETTINGS_CATEGORY_TAB,
  payload: { selectedDealerProfileSettingsCategory },
});

/**
 * @description
 * used to inform the user that saving changes is necessary
 * the modal is opened when a user tries to switch to another branch in the dropdown
 * without saving changes
 *
 * @param {object}  param0
 * @param {boolean} param0.modalIsVisible             - true if modal should open, false if it should close
 * @param {string}  [param.nextSelectedDealerId]      - dealerId selected by user
 * @returns {{type: 'SET_SAVE_CHANGES_MODAL_IS_VISIBLE', payload: {modalIsVisible: boolean, nextSelectedDealerId: string}}}
 */
export const setSaveChangesModalIsVisible = ({
  modalIsVisible,
  nextSelectedDealerId,
}) => ({
  type: AT.SET_SAVE_CHANGES_MODAL_IS_VISIBLE,
  payload: { modalIsVisible, nextSelectedDealerId },
});

/**
 * @description
 * used to show a confirmation animation if the user saves changes
 * in save changes modal
 *
 * @param {object}  param0
 * @param {boolean} param0.showConfirmationAnimation
 * @returns {{type: 'SET_SAVE_CHANGES_MODAL_IS_VISIBLE', payload: {showConfirmationAnimation: boolean}}}
 */
export const setShowConfirmationAnimation = ({
  showConfirmationAnimation,
}) => ({
  type: AT.SET_SHOW_CONFIRMATION_ANIMATION,
  payload: { showConfirmationAnimation },
});
